import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api/axios';
import { useToast } from '../context/ToastContext';
import { AuthContext } from '../context/AuthContext';
import { TenantContext } from '../context/TenantContext';
import GradePicker from '../components/GradePicker/GradePicker';
import { Icon } from '../components/SvgIcons';
import MyTemplatesList from '../components/MyTemplatesList';
import './PaperTemplates.css';

const iconBtn = { display: 'inline-flex', alignItems: 'center', gap: '0.4rem' };

// K-12 (school) tenants restrict the academic grade to senior classes. Other
// tenant types (university/college/enterprise) use their own academic schema
// — never force these "Class N" keys on them or the picker/distribution breaks.
const SCHOOL_CLASS_LEVELS = ['Class 11', 'Class 12'];

// Bridge the old hardcoded {class_11, class_12} shape to a dynamic
// {[classLevel]: percent} map. Existing saved templates keep working; new
// tenant_types (school K-12, UG/PG, etc.) emit canonical class_level strings.
const LEGACY_KEY_MAP = { class_11: 'Class 11', class_12: 'Class 12' };
function migrateClassWeightage(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    const canonical = LEGACY_KEY_MAP[k] || k;
    out[canonical] = Number(v) || 0;
  }
  return out;
}

const ExtractTemplateFromPDF = ({ embedded = false }) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext) || {};
  const { tenantType } = useContext(TenantContext) || {};
  const allowedClassLevels = tenantType === 'school' ? SCHOOL_CLASS_LEVELS : null;
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractedStructure, setExtractedStructure] = useState(null);
  const [saving, setSaving] = useState(false);
  const [autoAdjustEnabled, setAutoAdjustEnabled] = useState(false);
  const [activeTab, setActiveTab] = useState('create');
  const [formData, setFormData] = useState({
    template_name: '',
    description: '',
    questions: [],
    is_shared: false,
    difficulty_weightage: {
      easy: 30,
      medium: 50,
      hard: 20
    },
    // {[classLevelString]: percent} — keys are whatever GradePicker emits
    // (e.g. "Class 11", "UG FY Sem I"). Discovered dynamically from questions.
    class_weightage: {}
  });

  // Helper function to safely get difficulty weightage
  const getDifficultyWeightage = () => {
    return formData.difficulty_weightage || { easy: 30, medium: 50, hard: 20 };
  };

  // Safely get the current class_weightage map (keyed by class_level string)
  const getClassWeightage = () => formData.class_weightage || {};

  // Discover distinct class_level strings actually in use across the template's
  // questions/subquestions. Sorted for stable rendering. This drives the
  // dynamic N-class weightage UI.
  const getDistinctClassLevels = () => {
    const seen = new Set();
    formData.questions.forEach(q => {
      if (q.has_subquestions && q.subquestions) {
        q.subquestions.forEach(sq => {
          if (sq.class_level) seen.add(sq.class_level);
        });
      } else if (q.class_level) {
        seen.add(q.class_level);
      }
    });
    // Always include any class that already has a weightage allocation, even
    // if no question is tagged to it yet — lets users pre-set distribution
    // before tagging questions.
    Object.keys(formData.class_weightage || {}).forEach(k => seen.add(k));
    return Array.from(seen).sort();
  };

  const sumClassWeightage = () => {
    const w = getClassWeightage();
    return Object.values(w).reduce((s, v) => s + (Number(v) || 0), 0);
  };

  // Utility to shuffle arrays for randomization
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Helper function to generate unique template name
  const generateUniqueTemplateName = (baseName, existingTemplates) => {
    if (!baseName) return baseName;
    
    const templateNames = existingTemplates.map(t => t.template_name);
    
    // Check if base name already exists
    if (!templateNames.includes(baseName)) {
      return baseName;
    }

    // Check for existing numbered versions (e.g., "Template 1", "Template 2")
    // Pattern matches: "BaseName" or "BaseName 1", "BaseName 2", etc.
    const escapedBaseName = baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const namePattern = new RegExp(`^${escapedBaseName}(?:\\s+(\\d+))?$`);
    
    const numberedMatches = templateNames
      .map(name => {
        const match = name.match(namePattern);
        if (match) {
          // If no number group, it's the base name (count as 0)
          return match[1] ? parseInt(match[1], 10) : 0;
        }
        return null;
      })
      .filter(num => num !== null);

    // Find the next available number (starting from 1)
    if (numberedMatches.length === 0) {
      return `${baseName} 1`;
    }

    const maxNumber = Math.max(...numberedMatches);
    return `${baseName} ${maxNumber + 1}`;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setExtractedStructure(null);
    } else {
      showToast('Please select a valid PDF file', 'error');
      e.target.value = '';
    }
  };

  const handleExtract = async () => {
    if (!file) {
      showToast('Please select a PDF file first', 'error');
      return;
    }

    setExtracting(true);
    const formDataObj = new FormData();
    formDataObj.append('pdf', file);

    try {
      // Fetch existing templates to check for duplicates
      const templatesResponse = await API.get('/templates');
      const existingTemplates = templatesResponse.data.templates || [];

      const { data } = await API.post('/template-extraction/extract-structure', formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (data.success) {
        // Generate unique template name
        const baseTemplateName = data.structure.template_name || '';
        const uniqueTemplateName = generateUniqueTemplateName(baseTemplateName, existingTemplates);

        setExtractedStructure(data.structure);
        setFormData({
          template_name: uniqueTemplateName,
          description: data.structure.description || '',
          questions: data.structure.questions || [],
          difficulty_weightage: data.structure.difficulty_weightage || {
            easy: 30,
            medium: 50,
            hard: 20
          },
          class_weightage: migrateClassWeightage(data.structure.class_weightage)
        });
        showToast('Structure extracted successfully! Review and save.', 'success');
      }
    } catch (error) {
      console.error('Extraction error:', error);
      showToast(error.response?.data?.message || 'Failed to extract structure', 'error');
    } finally {
      setExtracting(false);
    }
  };

  const handleSave = async () => {
    if (!formData.template_name.trim()) {
      showToast('Please enter a template name', 'error');
      return;
    }

    if (formData.questions.length === 0) {
      showToast('No questions found to save', 'error');
      return;
    }

    // Ensure difficulty_weightage exists with defaults if missing
    const weightage = formData.difficulty_weightage || { easy: 30, medium: 50, hard: 20 };

    // Validate weightage totals 100%
    const totalWeightage = (weightage.easy || 0) + (weightage.medium || 0) + (weightage.hard || 0);
    if (totalWeightage !== 100) {
      showToast(`Difficulty weightage must total 100%. Current total: ${totalWeightage}%`, 'error');
      return;
    }

    // Validate class weightage totals 100% (only when any classes are in play —
    // a single-grade template doesn't need a weightage distribution).
    const classWeightage = getClassWeightage();
    const classKeys = Object.keys(classWeightage);
    if (classKeys.length > 1) {
      const totalClassWeightage = classKeys.reduce(
        (sum, k) => sum + (Number(classWeightage[k]) || 0),
        0,
      );
      if (totalClassWeightage !== 100) {
        showToast(`Class weightage must total 100%. Current total: ${totalClassWeightage}%`, 'error');
        return;
      }
    }

    // Update formData with validated weightage
    const dataToSave = {
      ...formData,
      is_shared: formData.is_shared,
      difficulty_weightage: weightage,
      class_weightage: classWeightage
    };

    setSaving(true);
    try {
      await API.post('/template-extraction/save-extracted-template', dataToSave);
      showToast('Template created successfully with difficulty weightage!', 'success');
      navigate('/paper-templates');
    } catch (error) {
      console.error('Save error:', error);
      showToast(error.response?.data?.message || 'Failed to save template', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleQuestionChange = (index, field, value) => {
    const newQuestions = [...formData.questions];
    newQuestions[index] = { ...newQuestions[index], [field]: value };

    // If toggling has_subquestions
    if (field === 'has_subquestions') {
      if (value && newQuestions[index].subquestions.length === 0) {
        newQuestions[index].subquestions = [
          {
            sub_number: 'a',
            marks: 2,
            difficulty: newQuestions[index].difficulty || 'medium',
            class_level: newQuestions[index].class_level || ''
          }
        ];
      }
    }

    // Update formData with new questions
    const updatedFormData = { ...formData, questions: newQuestions };

    // If difficulty changed and NOT in auto-adjust mode, update target weightage to match actual distribution
    if (field === 'difficulty' && !autoAdjustEnabled) {
      const actualDist = calculateActualDistribution(newQuestions);
      updatedFormData.difficulty_weightage = {
        easy: actualDist.easy,
        medium: actualDist.medium,
        hard: actualDist.hard
      };
    }

    setFormData(updatedFormData);
  };

  // Removed getDifficultyBadge function - not needed

  const handleSubQuestionChange = (qIndex, sqIndex, field, value) => {
    const newQuestions = [...formData.questions];
    newQuestions[qIndex].subquestions[sqIndex] = {
      ...newQuestions[qIndex].subquestions[sqIndex],
      [field]: value
    };

    // Update formData with new questions
    const updatedFormData = { ...formData, questions: newQuestions };

    // If difficulty changed and NOT in auto-adjust mode, update target weightage to match actual distribution
    if (field === 'difficulty' && !autoAdjustEnabled) {
      const actualDist = calculateActualDistribution(newQuestions);
      updatedFormData.difficulty_weightage = {
        easy: actualDist.easy,
        medium: actualDist.medium,
        hard: actualDist.hard
      };
    }

    setFormData(updatedFormData);
  };

  const handleAddQuestion = () => {
    const nextNumber = formData.questions.length + 1;
    setFormData({
      ...formData,
      questions: [...formData.questions, {
        question_number: `Q${nextNumber}`,
        question_type: 'mcq',
        difficulty: 'medium',
        class_level: '',
        has_subquestions: false,
        marks: 5,
        subquestions: []
      }]
    });
  };

  const handleRemoveQuestion = (index) => {
    const newQuestions = formData.questions.filter((_, i) => i !== index);
    const renumbered = newQuestions.map((q, i) => ({
      ...q,
      question_number: `Q${i + 1}`
    }));
    setFormData({ ...formData, questions: renumbered });
  };

  const handleAddSubQuestion = (qIndex) => {
    const newQuestions = [...formData.questions];
    const subCount = newQuestions[qIndex].subquestions.length;
    const nextLetter = String.fromCharCode(97 + subCount);

    newQuestions[qIndex].subquestions.push({
      sub_number: nextLetter,
      marks: 2,
      difficulty: newQuestions[qIndex].difficulty || 'medium',
      class_level: newQuestions[qIndex].class_level || ''
    });

    setFormData({ ...formData, questions: newQuestions });
  };

  const handleRemoveSubQuestion = (qIndex, sqIndex) => {
    const newQuestions = [...formData.questions];
    newQuestions[qIndex].subquestions = newQuestions[qIndex].subquestions.filter((_, i) => i !== sqIndex);

    // Renumber sub-questions
    newQuestions[qIndex].subquestions = newQuestions[qIndex].subquestions.map((sq, i) => ({
      ...sq,
      sub_number: String.fromCharCode(97 + i)
    }));

    setFormData({ ...formData, questions: newQuestions });
  };

  const ROMAN = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x'];
  const romanFor = (i) => ROMAN[i] || String(i + 1);

  // Marks for a sub-question: sum of its sub-parts when it has them, else its own marks.
  const subPartMarks = (sq) =>
    (sq.has_subquestions && Array.isArray(sq.subquestions) && sq.subquestions.length > 0)
      ? sq.subquestions.reduce((s, ssq) => s + (parseInt(ssq.marks) || 0), 0)
      : (parseInt(sq.marks) || 0);

  const handleAddSubPart = (qIndex, sqIndex) => {
    const newQuestions = [...formData.questions];
    const sq = newQuestions[qIndex].subquestions[sqIndex];
    sq.subquestions = sq.subquestions || [];
    sq.has_subquestions = true;
    sq.subquestions.push({
      sub_number: romanFor(sq.subquestions.length),
      marks: 1,
      difficulty: sq.difficulty || 'medium',
      class_level: sq.class_level || ''
    });
    setFormData({ ...formData, questions: newQuestions });
  };

  const handleRemoveSubPart = (qIndex, sqIndex, ssqIndex) => {
    const newQuestions = [...formData.questions];
    const sq = newQuestions[qIndex].subquestions[sqIndex];
    sq.subquestions = sq.subquestions
      .filter((_, i) => i !== ssqIndex)
      .map((ssq, i) => ({ ...ssq, sub_number: romanFor(i) }));
    if (sq.subquestions.length === 0) sq.has_subquestions = false;
    setFormData({ ...formData, questions: newQuestions });
  };

  const handleSubPartChange = (qIndex, sqIndex, ssqIndex, field, value) => {
    const newQuestions = [...formData.questions];
    newQuestions[qIndex].subquestions[sqIndex].subquestions[ssqIndex] = {
      ...newQuestions[qIndex].subquestions[sqIndex].subquestions[ssqIndex],
      [field]: value
    };
    setFormData({ ...formData, questions: newQuestions });
  };

  const getTotalMarks = () => {
    return formData.questions.reduce((sum, q) => {
      if (q.has_subquestions) {
        return sum + q.subquestions.reduce((subSum, sq) => subSum + subPartMarks(sq), 0);
      }
      return sum + (parseInt(q.marks) || 0);
    }, 0);
  };

  const getQuestionMarks = (question) => {
    if (question.has_subquestions) {
      return question.subquestions.reduce((sum, sq) => sum + subPartMarks(sq), 0);
    }
    return parseInt(question.marks) || 0;
  };

  // Helper function to calculate total marks from questions array
  const calculateTotalMarks = (questions) => {
    return questions.reduce((sum, q) => {
      if (q.has_subquestions) {
        return sum + q.subquestions.reduce((subSum, sq) => subSum + subPartMarks(sq), 0);
      }
      return sum + (parseInt(q.marks) || 0);
    }, 0);
  };

  // Helper function to calculate actual distribution from questions array
  const calculateActualDistribution = (questions) => {
    let easyMarks = 0, mediumMarks = 0, hardMarks = 0;

    questions.forEach(q => {
      if (q.has_subquestions && q.subquestions) {
        q.subquestions.forEach(sq => {
          const marks = parseInt(sq.marks) || 0;
          if (sq.difficulty === 'easy') easyMarks += marks;
          else if (sq.difficulty === 'hard') hardMarks += marks;
          else mediumMarks += marks;
        });
      } else {
        const marks = parseInt(q.marks) || 0;
        if (q.difficulty === 'easy') easyMarks += marks;
        else if (q.difficulty === 'hard') hardMarks += marks;
        else mediumMarks += marks;
      }
    });

    const totalMarks = calculateTotalMarks(questions);
    if (totalMarks === 0) return { easy: 0, medium: 0, hard: 0 };

    return {
      easy: Math.round((easyMarks / totalMarks) * 100),
      medium: Math.round((mediumMarks / totalMarks) * 100),
      hard: Math.round((hardMarks / totalMarks) * 100)
    };
  };

  // Calculate actual difficulty distribution based on questions
  const getActualDifficultyDistribution = () => {
    let easyMarks = 0, mediumMarks = 0, hardMarks = 0;

    formData.questions.forEach(q => {
      if (q.has_subquestions && q.subquestions) {
        q.subquestions.forEach(sq => {
          const marks = parseInt(sq.marks) || 0;
          if (sq.difficulty === 'easy') easyMarks += marks;
          else if (sq.difficulty === 'hard') hardMarks += marks;
          else mediumMarks += marks;
        });
      } else {
        const marks = parseInt(q.marks) || 0;
        if (q.difficulty === 'easy') easyMarks += marks;
        else if (q.difficulty === 'hard') hardMarks += marks;
        else mediumMarks += marks;
      }
    });

    const totalMarks = getTotalMarks();
    if (totalMarks === 0) return { easy: 0, medium: 0, hard: 0, easyMarks: 0, mediumMarks: 0, hardMarks: 0 };

    return {
      easy: Math.round((easyMarks / totalMarks) * 100),
      medium: Math.round((mediumMarks / totalMarks) * 100),
      hard: Math.round((hardMarks / totalMarks) * 100),
      easyMarks,
      mediumMarks,
      hardMarks
    };
  };

  // Auto-adjust question difficulties based on target weightage
  const autoAdjustDifficulties = (silent = false) => {
    const totalMarks = getTotalMarks();
    if (totalMarks === 0 || formData.questions.length === 0) {
      if (!silent) showToast('No questions to adjust', 'warning');
      return;
    }

    const weightage = getDifficultyWeightage();
    const targetEasyMarks = Math.round((totalMarks * weightage.easy) / 100);
    const targetMediumMarks = Math.round((totalMarks * weightage.medium) / 100);
    const targetHardMarks = totalMarks - targetEasyMarks - targetMediumMarks;

    // Collect all question items (questions and subquestions) with their marks
    const items = [];
    formData.questions.forEach((q, qIndex) => {
      if (q.has_subquestions && q.subquestions) {
        q.subquestions.forEach((sq, sqIndex) => {
          items.push({
            type: 'subquestion',
            qIndex,
            sqIndex,
            marks: parseInt(sq.marks) || 0,
            currentDifficulty: sq.difficulty || 'medium'
          });
        });
      } else {
        items.push({
          type: 'question',
          qIndex,
          marks: parseInt(q.marks) || 0,
          currentDifficulty: q.difficulty || 'medium'
        });
      }
    });

    // Sort by marks (descending) to assign harder questions to hard difficulty
    items.sort((a, b) => b.marks - a.marks);

    // Assign difficulties to match target distribution
    let easyMarks = 0,
      // eslint-disable-next-line no-unused-vars
      mediumMarks = 0,
      hardMarks = 0;
    const newQuestions = JSON.parse(JSON.stringify(formData.questions));

    items.forEach(item => {
      let assignedDifficulty = 'medium';

      // Assign based on which bucket needs more marks
      if (hardMarks < targetHardMarks) {
        assignedDifficulty = 'hard';
        hardMarks += item.marks;
      } else if (easyMarks < targetEasyMarks) {
        assignedDifficulty = 'easy';
        easyMarks += item.marks;
      } else {
        assignedDifficulty = 'medium';
        mediumMarks += item.marks;
      }

      // Apply the difficulty
      if (item.type === 'subquestion') {
        newQuestions[item.qIndex].subquestions[item.sqIndex].difficulty = assignedDifficulty;
      } else {
        newQuestions[item.qIndex].difficulty = assignedDifficulty;
      }
    });

    setFormData({ ...formData, questions: newQuestions });
    if (!silent) showToast('Question difficulties adjusted to match target weightage!', 'success');
  };

  // Handle difficulty weightage change with optional auto-adjust
  const handleWeightageChange = (level, value) => {
    const currentWeightage = getDifficultyWeightage();
    const newWeightage = { ...currentWeightage, [level]: value };
    setFormData({
      ...formData,
      difficulty_weightage: newWeightage
    });

    // If auto-adjust is enabled and total is 100%, automatically adjust questions
    if (autoAdjustEnabled) {
      const total = newWeightage.easy + newWeightage.medium + newWeightage.hard;
      if (total === 100) {
        // Use setTimeout to ensure state is updated first
        setTimeout(() => {
          autoAdjustDifficulties(true);
        }, 100);
      }
    }
  };

  const handleDivideMarks = (questionIndex) => {
    const newQuestions = [...formData.questions];
    const question = newQuestions[questionIndex];

    if (!question.has_subquestions || question.subquestions.length === 0) {
      showToast('Please add sub-questions first', 'warning');
      return;
    }

    // Prompt for total marks to divide
    const totalMarks = prompt(
      `Enter total marks to divide among ${question.subquestions.length} sub-questions:`,
      getQuestionMarks(question) || 10
    );

    if (totalMarks === null) return; // User cancelled

    const marks = parseInt(totalMarks);
    if (isNaN(marks) || marks <= 0) {
      showToast('Please enter a valid positive number', 'error');
      return;
    }

    // Divide marks evenly
    const numSubQuestions = question.subquestions.length;
    const marksPerSub = Math.floor(marks / numSubQuestions);
    const remainder = marks % numSubQuestions;

    // Distribute marks (first sub-question gets remainder)
    newQuestions[questionIndex].subquestions = question.subquestions.map((sq, i) => ({
      ...sq,
      marks: i === 0 ? marksPerSub + remainder : marksPerSub
    }));

    setFormData({ ...formData, questions: newQuestions });
    showToast(`Marks divided: ${marks} marks distributed among ${numSubQuestions} sub-questions`, 'success');
  };

  // Calculate actual class distribution from questions as a map keyed by the
  // canonical class_level string. Generalized from the legacy two-class
  // (Class 11/Class 12) hardcoded version.
  const getActualClassDistribution = () => {
    const marksByClass = {};
    formData.questions.forEach(q => {
      if (q.has_subquestions && q.subquestions) {
        q.subquestions.forEach(sq => {
          const marks = parseInt(sq.marks, 10) || 0;
          const key = sq.class_level || '';
          if (!key) return;
          marksByClass[key] = (marksByClass[key] || 0) + marks;
        });
      } else {
        const marks = parseInt(q.marks, 10) || 0;
        const key = q.class_level || '';
        if (!key) return;
        marksByClass[key] = (marksByClass[key] || 0) + marks;
      }
    });

    const totalMarks = getTotalMarks();
    const percentByClass = {};
    if (totalMarks === 0) {
      return { marksByClass, percentByClass };
    }
    Object.entries(marksByClass).forEach(([k, marks]) => {
      percentByClass[k] = Math.round((marks / totalMarks) * 100);
    });
    return { marksByClass, percentByClass };
  };

  // Auto-adjust question class_levels to match the target weightage map.
  // Distributes items across N classes (was hardcoded to 2). Uses a greedy
  // largest-deficit assignment over a shuffled item list.
  const autoAdjustClassLevels = () => {
    const totalMarks = getTotalMarks();
    if (totalMarks === 0 || formData.questions.length === 0) {
      showToast('No questions to adjust', 'warning');
      return;
    }

    const weightage = getClassWeightage();
    const classKeys = Object.keys(weightage);
    if (classKeys.length === 0) {
      showToast('Set at least one class weightage target first', 'warning');
      return;
    }

    // Compute target marks per class. Last class absorbs rounding remainder.
    const targets = {};
    let allocated = 0;
    classKeys.forEach((k, i) => {
      if (i === classKeys.length - 1) {
        targets[k] = totalMarks - allocated;
      } else {
        targets[k] = Math.round((totalMarks * (Number(weightage[k]) || 0)) / 100);
        allocated += targets[k];
      }
    });

    const items = [];
    formData.questions.forEach((q, qIndex) => {
      if (q.has_subquestions && q.subquestions) {
        q.subquestions.forEach((sq, sqIndex) => {
          items.push({
            type: 'subquestion',
            qIndex,
            sqIndex,
            marks: parseInt(sq.marks, 10) || 0,
          });
        });
      } else {
        items.push({
          type: 'question',
          qIndex,
          marks: parseInt(q.marks, 10) || 0,
        });
      }
    });

    const shuffledItems = shuffleArray(items);
    const filled = Object.fromEntries(classKeys.map(k => [k, 0]));
    const newQuestions = JSON.parse(JSON.stringify(formData.questions));

    shuffledItems.forEach(item => {
      // Pick the class with the largest remaining deficit (target - filled).
      let chosen = classKeys[0];
      let bestDeficit = -Infinity;
      classKeys.forEach(k => {
        const deficit = targets[k] - filled[k];
        if (deficit > bestDeficit) {
          bestDeficit = deficit;
          chosen = k;
        }
      });
      filled[chosen] += item.marks;

      if (item.type === 'subquestion') {
        newQuestions[item.qIndex].subquestions[item.sqIndex].class_level = chosen;
      } else {
        newQuestions[item.qIndex].class_level = chosen;
      }
    });

    setFormData({ ...formData, questions: newQuestions });
    showToast('Question class levels adjusted to match target weightage!', 'success');
  };

  return (
    <div className={embedded ? '' : 'dashboard'}>
      {!embedded && (
      <div className="dashboard-header">
        <div>
          <h1 className="dashboard-title" style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <Icon name="FileText" size={26} />
            <span>Extract Template from PDF</span>
          </h1>
          <p className="dashboard-subtitle" style={{ color: '#ffffff' }}>
            Upload a question paper PDF to automatically extract its structure
          </p>
        </div>
        <button className="btn btn-secondary" style={iconBtn} onClick={() => navigate('/paper-templates')}>
          <Icon name="ArrowLeft" size={16} />
          <span>Back to Templates</span>
        </button>
      </div>
      )}

      {!embedded && (
      <div className="card" style={{ padding: 0 }}>
        <div className="tpl-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'create'}
            className={`tpl-tab${activeTab === 'create' ? ' active' : ''}`}
            onClick={() => setActiveTab('create')}
          >
            <Icon name="Plus" />
            <span>Create</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'my-templates'}
            className={`tpl-tab${activeTab === 'my-templates' ? ' active' : ''}`}
            onClick={() => setActiveTab('my-templates')}
          >
            <Icon name="FileText" />
            <span>My Templates</span>
          </button>
        </div>
        {activeTab === 'my-templates' && <MyTemplatesList />}
      </div>
      )}

      {(embedded || activeTab === 'create') && (
      <>
      {/* Upload Section */}
      {!extractedStructure && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Icon name="Upload" size={20} />
              <span>Upload Question Paper PDF</span>
            </h2>
          </div>
          <div style={{ padding: '2rem' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label className="form-label">Select PDF File</label>
              <input
                type="file"
                accept="application/pdf"
                onChange={handleFileChange}
                className="form-input"
                style={{ padding: '0.75rem' }}
              />
              {file && (
                <p style={{ marginTop: '0.5rem', color: 'var(--success)', fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Icon name="Check" size={14} />
                  <span>Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)</span>
                </p>
              )}
            </div>

            <button
              className="btn btn-primary"
              onClick={handleExtract}
              disabled={!file || extracting}
              style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
            >
              {extracting ? (
                <>
                  <span className="spinner" style={{ marginRight: '0.5rem' }}></span>
                  Extracting Structure...
                </>
              ) : (
                <>
                  <Icon name="Search" size={16} />
                  <span>Extract Structure</span>
                </>
              )}
            </button>

            <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
              <h4 style={{ marginBottom: '0.5rem', color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <Icon name="Info" size={16} />
                <span>How it works:</span>
              </h4>
              <ul style={{ marginLeft: '1.5rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                <li>Upload a question paper PDF</li>
                <li>AI will analyze and extract the structure</li>
                <li>Review and edit the extracted questions</li>
                <li>Save as a reusable template</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Extracted Structure Review */}
      {extractedStructure && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Icon name="Edit" size={20} />
              <span>Review & Edit Extracted Structure</span>
            </h2>
            <button className="btn btn-secondary btn-sm" style={iconBtn} onClick={() => {
              setExtractedStructure(null);
              setFile(null);
              setFormData({
                template_name: '',
                description: '',
                questions: [],
                difficulty_weightage: {
                  easy: 30,
                  medium: 50,
                  hard: 20
                },
                class_weightage: {}
              });
            }}>
              <Icon name="RotateCcw" size={14} />
              <span>Upload Different PDF</span>
            </button>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} style={{ padding: '1.5rem' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label className="form-label">Template Name *</label>
              <input
                type="text"
                className="form-input"
                value={formData.template_name}
                onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                placeholder="e.g., Mid-Term Exam Template"
                required
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label className="form-label">Description</label>
              <textarea
                className="form-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description..."
                rows={2}
              />
            </div>

            {user?.role === 'examiner' && (
              <div style={{ marginBottom: '1.5rem', padding: '1rem 1.25rem', background: 'var(--bg-secondary)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>Share with other examiners</div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>Other examiners can view and use this template</div>
                </div>
                <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={formData.is_shared} onChange={(e) => setFormData({ ...formData, is_shared: e.target.checked })} style={{ opacity: 0, width: 0, height: 0 }} />
                  <span style={{
                    position: 'absolute', inset: 0, borderRadius: '12px', transition: '0.3s',
                    backgroundColor: formData.is_shared ? '#3b82f6' : '#cbd5e1'
                  }}>
                    <span style={{
                      position: 'absolute', left: formData.is_shared ? '22px' : '3px', top: '3px',
                      width: '18px', height: '18px', borderRadius: '50%', background: '#fff', transition: '0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                    }} />
                  </span>
                </label>
              </div>
            )}

            {/* Difficulty Weightage Section */}
            <div style={{ marginBottom: '1.5rem', padding: '1.5rem', background: 'var(--bg-secondary)', borderRadius: '12px', border: '2px solid var(--border-color)' }}>
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                  <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Icon name="Scale" size={18} />
                    <span>Difficulty Weightage Distribution</span>
                  </h4>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600', color: 'var(--primary)' }} title="Toggle between manual question adjustment and automatic percentage-based adjustment">
                    <input
                      type="checkbox"
                      checked={autoAdjustEnabled}
                      onChange={(e) => setAutoAdjustEnabled(e.target.checked)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                      <Icon name={autoAdjustEnabled ? 'Refresh' : 'Hand'} size={14} />
                      <span>{autoAdjustEnabled ? 'Auto-Adjust Mode' : 'Manual Mode'}</span>
                    </span>
                  </label>
                </div>
                <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  {autoAdjustEnabled
                    ? 'Auto-Adjust Mode: Change percentages to automatically redistribute question difficulties to match your targets.'
                    : 'Manual Mode: Change percentages as needed. When you change question difficulties, percentages will auto-update to reflect reality.'}
                </p>
                <details style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  <summary style={{ cursor: 'pointer', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                    <Icon name="Info" size={12} />
                    <span>How AI determines difficulty (based on cognitive depth, not length)</span>
                  </summary>
                  <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'var(--bg-primary)', borderRadius: '6px' }}>
                    <ul style={{ marginLeft: '1.5rem', lineHeight: '1.8', listStyle: 'none', paddingLeft: 0 }}>
                      <li style={{ marginBottom: '0.5rem' }}>
                        <strong style={{ color: 'var(--success)', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                          <Icon name="DifficultyDot" level="easy" />
                          <span>Easy:</span>
                        </strong> Requires only <strong>memorization/recall</strong>
                        <div style={{ marginLeft: '1.5rem', fontSize: '0.7rem', marginTop: '0.25rem' }}>
                          Can answer by remembering facts (e.g., "What is...?", "Define...", "List...")
                        </div>
                      </li>
                      <li style={{ marginBottom: '0.5rem' }}>
                        <strong style={{ color: 'var(--warning)', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                          <Icon name="DifficultyDot" level="medium" />
                          <span>Medium:</span>
                        </strong> Requires <strong>understanding & application</strong>
                        <div style={{ marginLeft: '1.5rem', fontSize: '0.7rem', marginTop: '0.25rem' }}>
                          Must understand concepts and apply them (e.g., "Why...?", "How...?", "Compare...")
                        </div>
                      </li>
                      <li>
                        <strong style={{ color: 'var(--danger)', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                          <Icon name="DifficultyDot" level="hard" />
                          <span>Hard:</span>
                        </strong> Requires <strong>deep thinking & synthesis</strong>
                        <div style={{ marginLeft: '1.5rem', fontSize: '0.7rem', marginTop: '0.25rem' }}>
                          Must integrate concepts and think critically (e.g., "Evaluate...", "Derive...", "Design...")
                        </div>
                      </li>
                    </ul>
                    <div style={{ marginTop: '0.75rem', padding: '0.5rem', background: 'var(--warning-light)', borderRadius: '4px', fontSize: '0.7rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                      <Icon name="Warning" size={14} />
                      <span><strong>Important:</strong> Difficulty is based on <strong>depth of understanding</strong> required, NOT answer length or question type!</span>
                    </div>
                  </div>
                </details>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                {/* Easy */}
                <div style={{ padding: '1rem', background: '#d4edda', borderRadius: '8px', border: '2px solid #28a745' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <label style={{ fontSize: '0.875rem', fontWeight: '700', color: '#155724', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                      <Icon name="DifficultyDot" level="easy" />
                      <span>Easy</span>
                    </label>
                    {formData.questions.length > 0 && (() => {
                      const actual = getActualDifficultyDistribution();
                      return (
                        <span style={{ fontSize: '0.75rem', color: '#155724', fontWeight: '600' }}>
                          {actual.easyMarks}/{getTotalMarks()} marks
                        </span>
                      );
                    })()}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="number"
                      className="form-input"
                      value={getDifficultyWeightage().easy}
                      onChange={(e) => {
                        const value = Math.max(0, Math.min(100, parseInt(e.target.value) || 0));
                        handleWeightageChange('easy', value);
                      }}
                      min="0"
                      max="100"
                      step="1"
                      style={{
                        padding: '0.5rem',
                        textAlign: 'center',
                        fontWeight: '700',
                        fontSize: '1.1rem'
                      }}
                      title={autoAdjustEnabled ? 'Edit to auto-adjust questions to match this percentage' : 'Edit target percentage (updates when you change question difficulties)'}
                    />
                    <span style={{ fontWeight: '700', color: '#155724', fontSize: '1.1rem' }}>%</span>
                  </div>
                </div>

                {/* Medium */}
                <div style={{ padding: '1rem', background: '#fff3cd', borderRadius: '8px', border: '2px solid #ffc107' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <label style={{ fontSize: '0.875rem', fontWeight: '700', color: '#856404', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                      <Icon name="DifficultyDot" level="medium" />
                      <span>Medium</span>
                    </label>
                    {formData.questions.length > 0 && (() => {
                      const actual = getActualDifficultyDistribution();
                      return (
                        <span style={{ fontSize: '0.75rem', color: '#856404', fontWeight: '600' }}>
                          {actual.mediumMarks}/{getTotalMarks()} marks
                        </span>
                      );
                    })()}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="number"
                      className="form-input"
                      value={getDifficultyWeightage().medium}
                      onChange={(e) => {
                        const value = Math.max(0, Math.min(100, parseInt(e.target.value) || 0));
                        handleWeightageChange('medium', value);
                      }}
                      min="0"
                      max="100"
                      step="1"
                      style={{
                        padding: '0.5rem',
                        textAlign: 'center',
                        fontWeight: '700',
                        fontSize: '1.1rem'
                      }}
                      title={autoAdjustEnabled ? 'Edit to auto-adjust questions to match this percentage' : 'Edit target percentage (updates when you change question difficulties)'}
                    />
                    <span style={{ fontWeight: '700', color: '#856404', fontSize: '1.1rem' }}>%</span>
                  </div>
                </div>

                {/* Hard */}
                <div style={{ padding: '1rem', background: '#f8d7da', borderRadius: '8px', border: '2px solid #dc3545' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <label style={{ fontSize: '0.875rem', fontWeight: '700', color: '#721c24', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                      <Icon name="DifficultyDot" level="hard" />
                      <span>Hard</span>
                    </label>
                    {formData.questions.length > 0 && (() => {
                      const actual = getActualDifficultyDistribution();
                      return (
                        <span style={{ fontSize: '0.75rem', color: '#721c24', fontWeight: '600' }}>
                          {actual.hardMarks}/{getTotalMarks()} marks
                        </span>
                      );
                    })()}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="number"
                      className="form-input"
                      value={getDifficultyWeightage().hard}
                      onChange={(e) => {
                        const value = Math.max(0, Math.min(100, parseInt(e.target.value) || 0));
                        handleWeightageChange('hard', value);
                      }}
                      min="0"
                      max="100"
                      step="1"
                      style={{
                        padding: '0.5rem',
                        textAlign: 'center',
                        fontWeight: '700',
                        fontSize: '1.1rem'
                      }}
                      title={autoAdjustEnabled ? 'Edit to auto-adjust questions to match this percentage' : 'Edit target percentage (updates when you change question difficulties)'}
                    />
                    <span style={{ fontWeight: '700', color: '#721c24', fontSize: '1.1rem' }}>%</span>
                  </div>
                </div>
              </div>

              {/* Total Indicator */}
              <div style={{
                padding: '0.75rem 1rem',
                background: (() => {
                  const weightage = getDifficultyWeightage();
                  const total = weightage.easy + weightage.medium + weightage.hard;
                  return total === 100 ? 'var(--success-light)' : 'var(--danger-light)';
                })(),
                borderRadius: '8px',
                border: `2px solid ${(() => {
                  const weightage = getDifficultyWeightage();
                  const total = weightage.easy + weightage.medium + weightage.hard;
                  return total === 100 ? 'var(--success)' : 'var(--danger)';
                })()}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ fontWeight: '600', fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                  Total Weightage:
                </span>
                <span style={{
                  fontWeight: '700',
                  fontSize: '1.25rem',
                  color: (() => {
                    const weightage = getDifficultyWeightage();
                    const total = weightage.easy + weightage.medium + weightage.hard;
                    return total === 100 ? 'var(--success)' : 'var(--danger)';
                  })(),
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                }}>
                  <span>
                    {(() => {
                      const weightage = getDifficultyWeightage();
                      return weightage.easy + weightage.medium + weightage.hard;
                    })()}%
                  </span>
                  {(() => {
                    const weightage = getDifficultyWeightage();
                    const ok = (weightage.easy + weightage.medium + weightage.hard) === 100;
                    return <Icon name={ok ? 'Check' : 'Warning'} size={16} />;
                  })()}
                </span>
              </div>

              {/* Auto-Adjust Button */}
              {formData.questions.length > 0 && (() => {
                const weightage = getDifficultyWeightage();
                return (weightage.easy + weightage.medium + weightage.hard) === 100;
              })() && (
                  <div style={{ marginTop: '1rem' }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={autoAdjustDifficulties}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                    >
                      <Icon name="Target" size={16} />
                      <span>Auto-Adjust Questions to Match Target Weightage</span>
                    </button>
                    <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--text-secondary)', textAlign: 'center' }}>
                      This will automatically reassign question difficulties to match your target percentages
                    </p>
                  </div>
                )}

              {(() => {
                const weightage = getDifficultyWeightage();
                const total = weightage.easy + weightage.medium + weightage.hard;
                return total !== 100 && (
                  <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'var(--warning-light)', borderRadius: '6px', fontSize: '0.875rem', color: 'var(--text-primary)', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Icon name="Warning" size={14} />
                    <span>Total must equal 100%. Current total: {total}%</span>
                  </div>
                );
              })()}
            </div>

            {/* Class Level Distribution — dynamic N-class weightage. Discovers
                which class_level values are in use across the template's
                questions and renders one input per distinct class. Add new
                classes via the GradePicker. */}
            {(() => {
              const distinct = getDistinctClassLevels();
              const weightage = getClassWeightage();
              const totalWeight = sumClassWeightage();
              const actual = getActualClassDistribution();
              return (
                <div style={{ marginBottom: '1.5rem', padding: '1.5rem', background: 'var(--bg-secondary)', borderRadius: '12px', border: '2px solid var(--border-color)' }}>
                  <h4 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: '700', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Icon name="GraduationCap" size={18} />
                    <span>Class Level Distribution</span>
                  </h4>
                  <p style={{ margin: '0 0 1rem 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    Set the percentage distribution across the academic grades used in this template.
                  </p>

                  <div style={{ marginBottom: '1rem' }}>
                    <GradePicker
                      value=""
                      onChange={(classLevel) => {
                        if (!classLevel || weightage[classLevel] !== undefined) return;
                        setFormData({
                          ...formData,
                          class_weightage: { ...weightage, [classLevel]: 0 },
                        });
                      }}
                      showBanner
                      allowClear={false}
                      allowedLevelKeys={allowedClassLevels}
                    />
                  </div>

                  {distinct.length === 0 ? (
                    <div style={{ padding: '1rem', background: 'var(--warning-light)', borderRadius: '6px', fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                      No class levels selected yet. Pick a grade above, or assign a grade to a question below — distribution sliders will appear here.
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: distinct.length === 1 ? '1fr' : 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                        {distinct.map(classKey => {
                          const marksForClass = actual.marksByClass[classKey] || 0;
                          return (
                            <div key={classKey} style={{ padding: '1rem', background: '#fdf6e2', borderRadius: '8px', border: '2px solid #d4af37' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem', gap: '0.5rem' }}>
                                <label style={{ fontSize: '0.875rem', fontWeight: '700', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                  <Icon name="GraduationCap" size={14} />
                                  <span>{classKey}</span>
                                </label>
                                {formData.questions.length > 0 && (
                                  <span style={{ fontSize: '0.75rem', color: '#0f172a', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                    {marksForClass}/{getTotalMarks()} marks
                                  </span>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <input
                                  type="number"
                                  className="form-input"
                                  value={weightage[classKey] ?? 0}
                                  onChange={(e) => {
                                    const v = Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0));
                                    setFormData({
                                      ...formData,
                                      class_weightage: { ...weightage, [classKey]: v },
                                    });
                                  }}
                                  min="0"
                                  max="100"
                                  step="1"
                                  style={{ padding: '0.5rem', textAlign: 'center', fontWeight: '700', fontSize: '1.1rem' }}
                                />
                                <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '1.1rem' }}>%</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const next = { ...weightage };
                                    delete next[classKey];
                                    setFormData({ ...formData, class_weightage: next });
                                  }}
                                  title="Remove this class from the distribution"
                                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#475569', display: 'inline-flex', alignItems: 'center' }}
                                  aria-label="Remove class"
                                >
                                  <Icon name="X" size={14} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div style={{
                        padding: '0.75rem 1rem',
                        background: totalWeight === 100 ? 'var(--success-light)' : 'var(--danger-light)',
                        borderRadius: '8px',
                        border: `2px solid ${totalWeight === 100 ? 'var(--success)' : 'var(--danger)'}`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}>
                        <span style={{ fontWeight: '600', fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                          Total Class Distribution:
                        </span>
                        <span style={{ fontWeight: '700', fontSize: '1.25rem', color: totalWeight === 100 ? 'var(--success)' : 'var(--danger)', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                          <span>{totalWeight}%</span>
                          <Icon name={totalWeight === 100 ? 'Check' : 'Warning'} size={16} />
                        </span>
                      </div>

                      {totalWeight !== 100 && (
                        <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'var(--warning-light)', borderRadius: '6px', fontSize: '0.875rem', color: 'var(--text-primary)', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                          <Icon name="Warning" size={14} />
                          <span>Total must equal 100%. Current total: {totalWeight}%</span>
                        </div>
                      )}

                      {formData.questions.length > 0 && totalWeight === 100 && distinct.length > 1 && (
                        <div style={{ marginTop: '1rem' }}>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={autoAdjustClassLevels}
                            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                          >
                            <Icon name="Target" size={16} />
                            <span>Auto-Adjust Questions to Match Class Distribution</span>
                          </button>
                          <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--text-secondary)', textAlign: 'center' }}>
                            This will automatically reassign question class levels to match your target percentages
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })()}

            <div className="form-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 className="form-section-title" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Icon name="FileText" size={18} />
                  <span>Extracted Questions</span>
                </h3>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <span className="total-marks-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Icon name="Target" size={14} />
                    <span>{formData.questions.length} Questions | {getTotalMarks()} Marks</span>
                  </span>
                  <button type="button" className="btn-add-question" onClick={handleAddQuestion} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Icon name="Plus" size={14} />
                    <span>Add Question</span>
                  </button>
                </div>
              </div>

              <div className="template-questions-list">
                {formData.questions.map((q, qIndex) => (
                  <div key={qIndex} className="template-question-item">
                    <div className="question-header">
                      <span className="question-number-badge">{q.question_number}</span>

                      <div style={{ flex: 1, display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: '600' }}>Type:</span>
                        <input
                          type="text"
                          className="form-input"
                          value={q.question_type}
                          onChange={(e) => handleQuestionChange(qIndex, 'question_type', e.target.value)}
                          placeholder="e.g., mcq, short_answer, case_study"
                          list={`question-types-${qIndex}`}
                          style={{ flex: 1, padding: '0.5rem' }}
                        />
                        <datalist id={`question-types-${qIndex}`}>
                          <option value="mcq">Multiple Choice (MCQ)</option>
                          <option value="short_answer">Short Answer</option>
                          <option value="long_answer">Long Answer</option>
                          <option value="true_false">True/False</option>
                          <option value="fill_in_blanks">Fill in the Blanks</option>
                          <option value="matching">Match the Following</option>
                          <option value="numerical">Numerical/Calculate</option>
                          <option value="diagram">Diagram/Draw</option>
                          <option value="case_study">Case Study</option>
                          <option value="practical">Practical</option>
                          <option value="coding">Coding</option>
                        </datalist>
                      </div>

                      {/* Class Level and Difficulty Selectors */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', position: 'relative' }}>
                        <GradePicker
                          value={q.class_level || ''}
                          onChange={(classLevel) => handleQuestionChange(qIndex, 'class_level', classLevel)}
                          allowedLevelKeys={allowedClassLevels}
                          compact
                        />

                        {/* Difficulty Selector */}
                        <select
                          className="form-input"
                          value={q.difficulty || 'medium'}
                          onChange={(e) => handleQuestionChange(qIndex, 'difficulty', e.target.value)}
                          style={{ padding: '0.5rem', minWidth: '120px' }}
                          title={q.difficulty_reason || 'AI-assigned difficulty based on question content'}
                        >
                          <option value="easy">Easy</option>
                          <option value="medium">Medium</option>
                          <option value="hard">Hard</option>
                        </select>
                        {q.difficulty_reason && (
                          <span
                            style={{
                              fontSize: '0.75rem',
                              color: 'var(--text-secondary)',
                              fontStyle: 'italic',
                              maxWidth: '150px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={q.difficulty_reason}
                          >
                            💡 {q.difficulty_reason}
                          </span>
                        )}
                      </div>

                      {!q.has_subquestions && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <input
                            type="number"
                            className="marks-input"
                            value={q.marks}
                            onChange={(e) => handleQuestionChange(qIndex, 'marks', parseInt(e.target.value))}
                            min="1"
                          />
                          <span style={{ fontWeight: '600', color: 'var(--success)' }}>marks</span>
                        </div>
                      )}

                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={q.has_subquestions}
                          onChange={(e) => handleQuestionChange(qIndex, 'has_subquestions', e.target.checked)}
                        />
                        <span>Has Sub-questions</span>
                      </label>

                      <button
                        type="button"
                        className="btn-delete-question"
                        onClick={() => handleRemoveQuestion(qIndex)}
                        title="Delete Question"
                      >
                        🗑️
                      </button>
                    </div>

                    {q.has_subquestions && (
                      <div className="subquestions-section">
                        <div className="subquestions-header">
                          <span className="subquestions-title">
                            Sub-questions ({getQuestionMarks(q)} marks total)
                          </span>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              type="button"
                              className="btn-divide-marks"
                              onClick={() => handleDivideMarks(qIndex)}
                              title="Divide marks evenly among sub-questions"
                            >
                              ➗ Divide Marks
                            </button>
                            <button
                              type="button"
                              className="btn-add-subquestion"
                              onClick={() => handleAddSubQuestion(qIndex)}
                            >
                              ➕ Add Sub-question
                            </button>
                          </div>
                        </div>

                        {q.subquestions.map((sq, sqIndex) => {
                          const hasParts = sq.has_subquestions && Array.isArray(sq.subquestions) && sq.subquestions.length > 0;
                          return (
                            <div key={sqIndex} className="subquestion-block">
                              <div className="subquestion-item">
                                <span className="subquestion-number">
                                  {sq.sub_number}
                                </span>

                                <span className="subquestion-type">{q.question_type}</span>

                                {!hasParts && (
                                  <>
                                    {/* Class Level (compact GradePicker) for Subquestion */}
                                    <GradePicker
                                      value={sq.class_level || ''}
                                      onChange={(classLevel) => handleSubQuestionChange(qIndex, sqIndex, 'class_level', classLevel)}
                                      allowedLevelKeys={allowedClassLevels}
                                      compact
                                    />

                                    {/* Difficulty Selector for Subquestion */}
                                    <select
                                      className="form-input"
                                      value={sq.difficulty || 'medium'}
                                      onChange={(e) => handleSubQuestionChange(qIndex, sqIndex, 'difficulty', e.target.value)}
                                      style={{ padding: '0.4rem', fontSize: '0.75rem', minWidth: '100px' }}
                                      title={sq.difficulty_reason || 'AI-assigned difficulty based on question content'}
                                    >
                                      <option value="easy">Easy</option>
                                      <option value="medium">Medium</option>
                                      <option value="hard">Hard</option>
                                    </select>
                                    {sq.difficulty_reason && (
                                      <span
                                        style={{
                                          fontSize: '0.7rem',
                                          color: 'var(--text-secondary)',
                                          fontStyle: 'italic',
                                          maxWidth: '120px',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap'
                                        }}
                                        title={sq.difficulty_reason}
                                      >
                                        💡 {sq.difficulty_reason}
                                      </span>
                                    )}
                                  </>
                                )}

                                <div className="subquestion-marks">
                                  <input
                                    type="number"
                                    className="marks-input"
                                    value={hasParts ? subPartMarks(sq) : sq.marks}
                                    onChange={(e) => handleSubQuestionChange(qIndex, sqIndex, 'marks', parseInt(e.target.value))}
                                    min="1"
                                    disabled={hasParts}
                                    title={hasParts ? 'Total of sub-parts' : undefined}
                                  />
                                  <span style={{ fontWeight: '600', color: 'var(--success)' }}>marks</span>
                                </div>

                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => handleAddSubPart(qIndex, sqIndex)}
                                  title="Add a sub-part (third level)"
                                >
                                  ➕ Sub-part
                                </button>

                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm"
                                  onClick={() => handleRemoveSubQuestion(qIndex, sqIndex)}
                                >
                                  ✕
                                </button>
                              </div>

                              {hasParts && (
                                <div className="subparts-section" style={{ marginLeft: '1.5rem', marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                  {sq.subquestions.map((ssq, ssqIndex) => (
                                    <div key={ssqIndex} className="subquestion-item">
                                      <span className="subquestion-number">{sq.sub_number}.{ssq.sub_number}</span>
                                      <span className="subquestion-type">{q.question_type}</span>
                                      <GradePicker
                                        value={ssq.class_level || ''}
                                        onChange={(classLevel) => handleSubPartChange(qIndex, sqIndex, ssqIndex, 'class_level', classLevel)}
                                        allowedLevelKeys={allowedClassLevels}
                                        compact
                                      />
                                      <select
                                        className="form-input"
                                        value={ssq.difficulty || 'medium'}
                                        onChange={(e) => handleSubPartChange(qIndex, sqIndex, ssqIndex, 'difficulty', e.target.value)}
                                        style={{ padding: '0.4rem', fontSize: '0.75rem', minWidth: '100px' }}
                                      >
                                        <option value="easy">Easy</option>
                                        <option value="medium">Medium</option>
                                        <option value="hard">Hard</option>
                                      </select>
                                      <div className="subquestion-marks">
                                        <input
                                          type="number"
                                          className="marks-input"
                                          value={ssq.marks}
                                          onChange={(e) => handleSubPartChange(qIndex, sqIndex, ssqIndex, 'marks', parseInt(e.target.value))}
                                          min="1"
                                        />
                                        <span style={{ fontWeight: '600', color: 'var(--success)' }}>marks</span>
                                      </div>
                                      <button
                                        type="button"
                                        className="btn btn-danger btn-sm"
                                        onClick={() => handleRemoveSubPart(qIndex, sqIndex, ssqIndex)}
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '2rem' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setExtractedStructure(null);
                  setFile(null);
                  setFormData({
                    template_name: '',
                    description: '',
                    questions: [],
                    difficulty_weightage: {
                      easy: 30,
                      medium: 50,
                      hard: 20
                    },
                    class_weightage: {}
                  });
                }}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                {saving ? (
                  <>
                    <span className="spinner" style={{ marginRight: '0.5rem' }}></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <Icon name="Save" size={16} />
                    <span>Save Template</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default ExtractTemplateFromPDF;